import React from 'react';
import { ArrowRight, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-16 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1610484826967-09c5720778c7"
          alt="Online learning"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70"></div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-teal-500/20 backdrop-blur-sm border border-teal-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="h-4 w-4 text-teal-400" />
            <span className="text-teal-300 text-sm font-medium">Professional Online Tutoring</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Excel in Your 
            <span className="text-teal-400"> Higher Education</span>
            <br />Journey
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
            Expert online tutoring for Health & Social Care, Counselling, and Social Sciences. 
            Achieve your academic goals with personalized support from an experienced educator.
          </p>

          {/* Key Points */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-semibold">10+ Years Experience</p>
                <p className="text-gray-400 text-sm">Teaching higher education students</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-semibold">Flexible Scheduling</p>
                <p className="text-gray-400 text-sm">Evenings and weekends available</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-semibold">Proven Results</p>
                <p className="text-gray-400 text-sm">95% student satisfaction rate</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-semibold">All Levels Welcome</p>
                <p className="text-gray-400 text-sm">Foundation to postgraduate</p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg group"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('courses')}
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
            >
              View Courses
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 pt-8 border-t border-gray-700">
            <p className="text-gray-400 text-sm mb-4">Trusted by students at:</p>
            <div className="flex flex-wrap gap-6 text-gray-500 text-sm">
              <span>Open University</span>
              <span className="hidden sm:inline">•</span>
              <span>Pearson BTEC</span>
              <span className="hidden sm:inline">•</span>
              <span>CACHE Qualifications</span>
              <span className="hidden sm:inline">•</span>
              <span>Various UK Universities</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
